<template>
	<v-dialog
		v-model="show"
		max-width="700"
		persistent
		@after-enter="focusApplyBtn"
	>
		<v-card>
			<v-toolbar color="primary" dark flat>
				<v-toolbar-title>Special Characters Found</v-toolbar-title>
				<v-hover-icon class="mr-3" />
			</v-toolbar>

			<v-card-text>
				<p class="text-body-2 mb-4">
					<span v-if="invalidCount > 0">
						Found {{ pluralize("invalid character", invalidCount, true) }}
						that must be removed.
					</span>
					<span v-if="unidentifiedCount > 0">
						<span v-if="invalidCount > 0"> Also found </span>
						<span v-else>Found </span>
						{{ pluralize("unrecognized character", unidentifiedCount, true) }}
						- likely a symbol that was dropped. Choose a replacement below.
					</span>
					<span v-if="smartDecimalCount > 0">
						<span v-if="invalidCount > 0 || unidentifiedCount > 0">
							Also found
						</span>
						<span v-else>Found </span>
						{{ pluralize("character", smartDecimalCount, true) }}
						between digits - likely a decimal point.
					</span>
					<span v-if="normalizationCount > 0">
						<span
							v-if="
								invalidCount > 0 ||
								unidentifiedCount > 0 ||
								smartDecimalCount > 0
							"
						>
							Additionally,
						</span>
						<span v-else>Found </span>
						{{ pluralize("special character", normalizationCount, true) }}
						can be converted to standard text.
					</span>
				</p>

				<!-- Before/After Comparison -->
				<v-card variant="outlined" class="mb-4">
					<v-card-text class="py-3">
						<div class="d-flex ga-3">
							<div
								class="text-subtitle-2 font-weight-bold text-no-wrap"
								style="min-width: 70px"
							>
								Original:
							</div>
							<div class="flex-grow-1 text-break text-mono text-body-1">
								{{ originalText }}
							</div>
						</div>
						<v-divider class="my-2" />
						<div class="d-flex ga-3">
							<div
								class="text-subtitle-2 font-weight-bold text-no-wrap"
								style="min-width: 70px"
							>
								Preview:
							</div>
							<div
								class="flex-grow-1 text-break text-mono text-body-1 font-weight-medium"
							>
								<template
									v-for="(segment, index) in previewSegments"
									:key="index"
								>
									<span
										:class="{
											'highlight-change': segment.changed,
										}"
									>
										{{ segment.text }}
									</span>
								</template>
							</div>
						</div>
					</v-card-text>
				</v-card>

				<!-- Quick Actions -->
				<div
					v-if="normalizationCount > 0 || smartDecimalCount > 0"
					class="d-flex justify-space-between mb-3"
				>
					<span class="text-subtitle-2">Choose Replacements:</span>
					<div>
						<v-btn size="small" variant="text" @click="selectAll">
							Accept All
						</v-btn>
						<v-btn size="small" variant="text" @click="selectNone">
							Keep All Original
						</v-btn>
					</div>
				</div>

				<!-- Replacement List -->
				<v-list density="compact" class="replacement-list">
					<!-- Invalid Characters (MUST be replaced) -->
					<template v-if="invalidReplacements.length > 0">
						<v-list-subheader class="text-error font-weight-bold">
							Invalid Characters (will be removed automatically)
						</v-list-subheader>
						<v-list-item
							v-for="(replacement, index) in invalidReplacements"
							:key="`invalid-${index}`"
							class="border-b disabled-item"
						>
							<v-list-item-title>
								<div class="d-flex align-center">
									<code
										class="pa-2 rounded text-h6 font-weight-bold text-error char-badge"
										>{{
											replacement.originalChar === " "
												? "·"
												: replacement.originalChar
										}}</code
									>
									<v-icon size="small" class="mx-2"> mdi-arrow-right </v-icon>
									<code
										class="pa-2 rounded text-h6 font-weight-bold text-success char-badge"
									>
										{{
											replacement.newValue === ""
												? "(removed)"
												: replacement.newValue
										}}
									</code>
									<span class="text-caption text-fg-2 ml-3">
										{{ replacement.description }}
									</span>
								</div>
							</v-list-item-title>

							<template #append>
								<v-chip color="error" size="small" variant="flat">
									Required
								</v-chip>
							</template>
						</v-list-item>
					</template>

					<template v-if="unidentifiedReplacements.length > 0">
						<v-list-subheader class="text-warning font-weight-bold">
							Unrecognized Characters (choose a replacement)
						</v-list-subheader>
						<v-list-item
							v-for="(replacement, index) in unidentifiedReplacements"
							:key="`unidentified-${index}`"
							class="border-b decimal-item"
						>
							<v-list-item-title>
								<div class="d-flex align-center mb-2">
									<img
										v-if="replacement.snippet"
										:src="replacement.snippet"
										class="glyph-snippet"
										alt="the dropped character as it appears in the PDF"
									/>
									<code
										v-else
										class="pa-2 rounded text-h6 font-weight-bold text-error char-badge"
										>?</code
									>
									<v-icon size="small" class="mx-2"> mdi-arrow-right </v-icon>
									<v-text-field
										v-model="replacement.editableValue"
										density="compact"
										hide-details
										variant="outlined"
										class="editable-replacement"
										placeholder="(removed)"
									/>
									<span class="text-caption text-fg-2 ml-3">
										This character could not be read from the PDF.
									</span>
								</div>
								<div class="d-flex align-center flex-wrap ga-1">
									<span class="text-caption text-fg-2 mr-1"> Quick pick: </span>
									<v-chip
										v-for="pick in unidentifiedQuickPicks"
										:key="pick.label"
										size="small"
										variant="outlined"
										class="qp-chip"
										:color="
											replacement.editableValue === pick.value
												? 'primary'
												: 'default'
										"
										@click="replacement.editableValue = pick.value"
									>
										{{ pick.label }}
									</v-chip>
									<symbol-picker
										class="ml-1"
										@insert="(char) => (replacement.editableValue = char)"
									/>
								</div>
							</v-list-item-title>

							<template #append>
								<v-chip
									:color="replacement.editableValue ? 'success' : 'default'"
									size="small"
									variant="flat"
								>
									{{ replacement.editableValue ? "Replacing" : "Removing" }}
								</v-chip>
							</template>
						</v-list-item>
					</template>

					<!-- Smart Decimal (User chooses "." or remove) -->
					<template v-if="smartDecimalReplacements.length > 0">
						<v-list-subheader class="text-warning font-weight-bold">
							Decimal Points
						</v-list-subheader>
						<v-list-item
							v-for="(replacement, index) in smartDecimalReplacements"
							:key="`decimal-${index}`"
							class="border-b decimal-item"
						>
							<v-list-item-title>
								<div class="d-flex align-center mb-2">
									<code
										class="pa-2 rounded text-h6 font-weight-bold text-error char-badge"
										>{{
											replacement.originalChar === " "
												? "·"
												: replacement.originalChar
										}}</code
									>
									<v-icon size="small" class="mx-2"> mdi-arrow-right </v-icon>
									<code
										class="pa-2 rounded text-h6 font-weight-bold text-success char-badge"
									>
										{{ replacement.choice === "replace" ? "." : "(removed)" }}
									</code>
									<span class="text-caption text-fg-2 ml-3">
										{{ replacement.description }}
									</span>
								</div>
								<v-radio-group
									v-model="replacement.choice"
									hide-details
									density="compact"
									inline
									class="mt-0"
								>
									<v-radio label="Replace with '.'" value="replace" />
									<v-radio label="Remove" value="remove" />
								</v-radio-group>
							</v-list-item-title>
						</v-list-item>
					</template>

					<!-- Normalization (User can keep or replace) -->
					<template v-if="normalizationReplacements.length > 0">
						<v-list-subheader class="text-primary font-weight-bold">
							Special Characters (optional conversion)
						</v-list-subheader>
						<v-list-item
							v-for="(replacement, index) in normalizationReplacements"
							:key="`norm-${index}`"
							class="border-b"
						>
							<template #prepend>
								<v-checkbox
									v-model="replacement.useReplacement"
									hide-details
									color="primary"
								/>
							</template>

							<v-list-item-title>
								<div class="d-flex align-center mb-2">
									<code
										class="pa-2 rounded text-h6 font-weight-bold text-error char-badge"
										>{{
											replacement.originalChar === " "
												? "·"
												: replacement.originalChar
										}}</code
									>
									<v-icon size="small" class="mx-2"> mdi-arrow-right </v-icon>
									<v-text-field
										v-model="replacement.editableValue"
										density="compact"
										hide-details
										variant="outlined"
										class="editable-replacement"
										placeholder="(removed)"
										:disabled="!replacement.useReplacement"
									/>
									<span class="text-caption text-fg-2 ml-3">
										{{ replacement.description }}
									</span>
								</div>
							</v-list-item-title>

							<template #append>
								<v-chip
									:color="replacement.useReplacement ? 'success' : 'default'"
									size="small"
									variant="flat"
								>
									{{ replacement.useReplacement ? "Normalizing" : "Keeping" }}
								</v-chip>
							</template>
						</v-list-item>
					</template>
				</v-list>
			</v-card-text>

			<v-card-actions class="pb-5">
				<v-spacer />
				<v-btn class="bg-grey" elevation="2" @click="cancel"> Cancel </v-btn>
				<v-btn
					ref="applyBtn"
					class="bg-secondary"
					elevation="2"
					@click="confirm"
				>
					Apply Changes
				</v-btn>
				<v-spacer />
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import VHoverIcon from "@/components/VHoverIcon";
import SymbolPicker from "@/components/SymbolPicker.vue";
import pluralize from "@/utils/pluralize";

export default {
	name: "TextSanitizationDialog",

	components: {
		VHoverIcon,
		SymbolPicker,
	},

	data() {
		return {
			show: false,
			originalText: "",
			replacementDetails: [],
			invalidReplacements: [],
			smartDecimalReplacements: [],
			normalizationReplacements: [],
			unidentifiedReplacements: [],
			resolve: null,
			// Common dropped-symbol replacements. Values match what the
			// normalize map would produce for the same symbol, so a fixed-up
			// character reads the same as one from a well-behaved PDF.
			unidentifiedQuickPicks: [
				{ label: ">=", value: ">=" },
				{ label: "<=", value: "<=" },
				{ label: ">", value: ">" },
				{ label: "<", value: "<" },
				{ label: "=", value: "=" },
				{ label: "+/-", value: "+/-" },
				{ label: "~", value: "~" },
				{ label: "Remove", value: "" },
			],
		};
	},

	computed: {
		invalidCount() {
			return this.invalidReplacements.length;
		},

		smartDecimalCount() {
			return this.smartDecimalReplacements.length;
		},

		normalizationCount() {
			return this.normalizationReplacements.length;
		},

		unidentifiedCount() {
			return this.unidentifiedReplacements.length;
		},

		allReplacements() {
			return [
				...this.invalidReplacements,
				...this.smartDecimalReplacements,
				...this.normalizationReplacements,
				...this.unidentifiedReplacements,
			];
		},

		previewText() {
			// Build the result string by applying replacements
			const sortedReplacements = [...this.allReplacements].sort(
				(a, b) => a.position - b.position,
			);

			let result = "";
			let lastPos = 0;

			for (const replacement of sortedReplacements) {
				const pos = replacement.position;

				// Add unchanged text before this replacement
				result += this.originalText.substring(lastPos, pos);

				// Determine the new value based on replacement type
				let newValue;
				if (replacement.type === "invalid") {
					newValue = replacement.newValue;
				} else if (replacement.type === "smart-decimal") {
					newValue = replacement.choice === "replace" ? "." : "";
				} else if (replacement.type === "normalization") {
					newValue = replacement.useReplacement
						? replacement.editableValue || ""
						: replacement.originalChar;
				} else if (replacement.type === "unidentified") {
					newValue = replacement.editableValue || "";
				}

				// Add the replacement value
				result += newValue;

				// Move past this character in the original text
				lastPos = pos + replacement.originalChar.length;
			}

			// Add remaining unchanged text
			result += this.originalText.substring(lastPos);

			return result;
		},

		previewSegments() {
			const segments = [];
			let lastPos = 0;

			// Sort replacements by position in original text
			const sortedReplacements = [...this.allReplacements].sort(
				(a, b) => a.position - b.position,
			);

			for (const replacement of sortedReplacements) {
				const pos = replacement.position;

				// Add unchanged text before this replacement
				if (pos > lastPos) {
					segments.push({
						text: this.originalText.substring(lastPos, pos),
						changed: false,
					});
				}

				let newValue;
				let isChanged = false;

				// Determine the new value and whether it changed
				if (replacement.type === "invalid") {
					newValue = replacement.newValue;
					isChanged = true;
				} else if (replacement.type === "smart-decimal") {
					newValue = replacement.choice === "replace" ? "." : "";
					isChanged = true;
				} else if (replacement.type === "normalization") {
					newValue = replacement.useReplacement
						? replacement.editableValue || ""
						: replacement.originalChar;
					isChanged = replacement.useReplacement;
				} else if (replacement.type === "unidentified") {
					newValue = replacement.editableValue || "";
					isChanged = true;
				}

				// Add the replacement (or original if not changed)
				segments.push({
					text: newValue,
					changed: isChanged,
				});

				// Move past this character in the original text
				lastPos = pos + replacement.originalChar.length;
			}

			// Add remaining unchanged text
			if (lastPos < this.originalText.length) {
				segments.push({
					text: this.originalText.substring(lastPos),
					changed: false,
				});
			}

			return segments;
		},
	},

	methods: {
		pluralize,

		open(originalText, sanitizedText, replacementDetails, sourceImages = []) {
			this.originalText = originalText;
			this.replacementDetails = replacementDetails || [];

			// Parse replacement details from select-text.js
			this.parseReplacements(replacementDetails, sourceImages);

			this.show = true;

			return new Promise((resolve) => {
				this.resolve = resolve;
			});
		},

		parseReplacements(replacementDetails, sourceImages = []) {
			// Now replacementDetails is an array of objects with:
			// { type, originalChar, newValue, position, description, unicodeCode }
			this.invalidReplacements = [];
			this.smartDecimalReplacements = [];
			this.normalizationReplacements = [];
			this.unidentifiedReplacements = [];

			// sourceImages holds one zoomed crop per unidentified character, in
			// the same order they appear, so we hand them out as we go.
			let snippetIndex = 0;

			for (const detail of replacementDetails) {
				if (detail.type === "invalid") {
					const replacement = {
						...detail,
					};
					this.invalidReplacements.push(replacement);
				} else if (detail.type === "smart-decimal") {
					const replacement = {
						...detail,
						choice: "replace", // Default to replacing with "."
					};
					this.smartDecimalReplacements.push(replacement);
				} else if (detail.type === "normalization") {
					const replacement = {
						...detail,
						useReplacement: true, // Default to normalizing
						editableValue: detail.newValue, // Initialize editable value
					};
					this.normalizationReplacements.push(replacement);
				} else if (detail.type === "unidentified") {
					const replacement = {
						...detail,
						editableValue: "", // Default to removal; user can type/pick
						snippet: sourceImages?.[snippetIndex] || null,
					};
					snippetIndex += 1;
					this.unidentifiedReplacements.push(replacement);
				}
			}
		},

		selectAll() {
			// Only affects normalization and smart decimals
			this.normalizationReplacements.forEach((r) => {
				r.useReplacement = true;
			});
			this.smartDecimalReplacements.forEach((r) => {
				r.choice = "replace";
			});
		},

		selectNone() {
			// Only affects normalization
			this.normalizationReplacements.forEach((r) => {
				r.useReplacement = false;
			});
			this.smartDecimalReplacements.forEach((r) => {
				r.choice = "remove";
			});
		},

		confirm() {
			this.show = false;
			this.resolve({
				confirmed: true,
				text: this.previewText,
			});
		},

		cancel() {
			this.show = false;
			this.resolve({
				confirmed: false,
				text: null,
			});
		},

		focusApplyBtn() {
			// Focus the primary action after the dialog's enter transition
			// completes so Enter acts as "accept" without manual click.
			// Vuetify 3 v-btn exposes the underlying element on $el; fall
			// back to querying in case the ref shape changes.
			const btn = this.$refs.applyBtn;
			const el = btn?.$el ?? btn;
			el?.focus?.();
		},
	},
};
</script>

<style scoped>
/* Custom mono font styling */
.text-mono {
	font-family: "Courier New", Courier, monospace;
	font-size: 15px;
	line-height: 1.6;
}

/* Highlighted changes in preview.
   DCTL-2293: `color` read --v-theme-success-darken-2, which is not a declared
   theme key. An undefined custom property makes the rgb() invalid, so the whole
   declaration was DROPPED and this text has been rendering the inherited ink,
   not a green - the rule looked live and did nothing.
   A value diff is the valence axis (COLOR.md axis 3), so the pair now names it
   outright, opaque per rule 4 rather than a 25% wash whose contrast depended on
   whatever sat behind it. Ink on tint is >=5.49:1 by construction. */
.highlight-change {
	background-color: rgb(var(--v-theme-valence-positive-tint));
	padding: 2px 4px;
	border-radius: 4px;
	font-weight: 700;
	color: rgb(var(--v-theme-valence-positive-ink));
}

/* Scrollable replacement list */
.replacement-list {
	max-height: 400px;
	overflow-y: auto;
}

/* Background colors for special item types */
.disabled-item {
	opacity: 0.8;
	background-color: rgb(var(--v-theme-valence-negative-tint));
}

.decimal-item {
	background-color: rgb(var(--v-theme-valence-attention-tint));
}

/* Light background for character badges */
.char-badge {
	background-color: rgba(var(--v-theme-on-surface), 0.06);
}

/* Editable replacement field styling */
.editable-replacement {
	max-width: 80px;
	min-width: 40px;
}

.editable-replacement :deep(.v-field__input) {
	font-family: "Courier New", Courier, monospace;
	font-size: 16px;
	font-weight: 700;
	padding: 8px 12px;
	min-height: 0;
}

.editable-replacement :deep(.v-field) {
	border-radius: 4px;
	/* Keep a white fill so the input stays distinct from the tinted
	   "unrecognized character" row background. */
	background-color: rgb(var(--v-theme-surface));
}

/* White fill on the quick-pick chips so they stand out from the tinted
   "unrecognized character" row (outlined chips are transparent by default).
   !important to beat Vuetify's outlined-variant transparent background. */
.qp-chip {
	background-color: rgb(var(--v-theme-surface)) !important;
}

/* Zoomed crop of the dropped glyph, shown in the unrecognized-char row in
   place of the "?" badge so the reviewer can see the actual source glyph. */
.glyph-snippet {
	height: 40px;
	max-width: 160px;
	object-fit: contain;
	background: #fff;
	border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
	border-radius: 4px;
}
</style>
